import React, { useCallback, useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { createPortal } from 'react-dom';
import { Icon } from '@/styles/Icon';

interface SnackbarProps {
  message: string;
  isOpen: boolean;
  onClose?: () => void;
  duration?: number;
  showCheckIcon?: boolean;
  showErrorIcon?: boolean;
  showCloseIcon?: boolean;
}

export const Snackbar: React.FC<SnackbarProps> = ({
  message,
  isOpen,
  onClose,
  duration = 3000,
  showCheckIcon = false,
  showErrorIcon = false,
  showCloseIcon = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      
      if (duration > 0) {
        const timer = setTimeout(() => {
          handleClose();
        }, duration);
        
        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }
  }, [isOpen, duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300); // 애니메이션 시간
  };

  if (!isOpen && !isVisible) return null;

  return createPortal(
    <SnackbarContainer $isVisible={isVisible}>
      <SnackbarContent>
        {showCheckIcon && (
            <Icon name="checkRounded" size={28} />
        )}
        {showErrorIcon && (
            <Icon name="errorRounded" size={28} />
        )}
        <Message>{message}</Message>
        {showCloseIcon && (
          <CloseButton onClick={handleClose}>
            <Icon name='close' size={22} />
          </CloseButton>
        )}
      </SnackbarContent>
    </SnackbarContainer>,
    document.body
  );
};

// 스낵바 관리를 위한 커스텀 훅
interface SnackbarState extends SnackbarProps {}
export const useSnackbar = () => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    isOpen: false,
    message: '',
    showCheckIcon: false,
    showErrorIcon: false,
    showCloseIcon: false,
    duration: 3000,
  });
  
  const showSnackbar = useCallback((
    message: string, 
    options?: Partial<SnackbarState>
  ) => {
    setSnackbar({
      isOpen: true,
      message,
      showCheckIcon: options?.showCheckIcon || false,
      showErrorIcon: options?.showErrorIcon || false,
      showCloseIcon: options?.showCloseIcon !== false,
      duration: options?.duration || 3000,
    });
  }, []);

  const hideSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, isOpen: false }));
  }, []);

  return {
    snackbar,
    showSnackbar,
    hideSnackbar,
  };
};

// 스타일 컴포넌트들
const SnackbarContainer = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(${({ $isVisible }) => $isVisible ? '0' : '100px'});
  opacity: ${({ $isVisible }) => $isVisible ? '1' : '0'};
  transition: all 0.3s ease;
  z-index: ${({ theme }) => theme.zIndex.modal + 10};
  pointer-events: ${({ $isVisible }) => $isVisible ? 'auto' : 'none'};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.bgWhite};
  box-shadow: 0 4px 12px 0 rgba(169, 169, 169, 0.25);
`;

const SnackbarContent = styled.div`
  display: flex;
  width: 489px;
  max-width: max-content;
  padding: 16.44px;
  align-items: center;
  gap: 10.96px;
  border-radius: 6.85px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: calc(100vw - 48px);
    max-width: max-content;
  }
`;

const Message = styled.span`
  flex: 1;
  color: ${({ theme }) => theme.colors.fontBlack};
  font-family: ${({ theme }) => theme.fonts.pretendard};
  font-size: 19.18px;
  font-style: normal;
  font-weight: 400;
  line-height: 27.4px;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 4px;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
  
  &:focus {
    outline: none;
  }
`;

// 사용 예시를 위한 헬퍼 함수
export const showCopySnackbar = (showSnackbar: ReturnType<typeof useSnackbar>['showSnackbar']) => {
  showSnackbar('Copied to clipboard', {
    showCheckIcon: true,
    showCloseIcon: true,
    duration: 3000,
  });
};